import { useEffect, useState } from 'react';
import db, { collection, doc, limit, orderBy, query, where } from '../firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { subscribeCollection, subscribeDoc } from './useResilientSnapshot';

// The academy's website lead bot config (leadBots/{academyId}) — see
// functions/leadBot.js. Created server-side by leadBotEnsure the first time
// an admin opens the Lead Bot page.
export function useLeadBotConfig() {
  const { academyId } = useAuth();
  const [bot, setBot] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!academyId) { setLoading(false); return undefined; }
    return subscribeDoc(doc(db, 'leadBots', academyId), setBot, setLoading);
  }, [academyId]);
  return { bot, loading };
}

// Every staff member's call availability doc in the academy.
export function useLeadBotAvailability() {
  const { academyId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!academyId) { setLoading(false); return undefined; }
    return subscribeCollection(query(collection(db, 'leadBotAvailability'), where('academyId', '==', academyId)), setRows, setLoading);
  }, [academyId]);
  return { rows, loading };
}

export function useLeadBotChat(chatId) {
  const [chat, setChat] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!chatId) { setChat(null); setLoading(false); return undefined; }
    setLoading(true);
    return subscribeDoc(doc(db, 'leadBotChats', chatId), setChat, setLoading);
  }, [chatId]);
  return { chat, loading };
}

// Chats where a visitor is waiting for a person — the sidebar badge.
export function useLeadBotWaitingCount(enabled) {
  const { academyId } = useAuth();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!enabled || !academyId) { setRows([]); return undefined; }
    const q = query(collection(db, 'leadBotChats'), where('academyId', '==', academyId), where('status', '==', 'waitingHuman'));
    return subscribeCollection(q, setRows, () => {});
  }, [enabled, academyId]);
  return rows.filter((r) => !r.test).length;
}

// Booked calls (lead bot and booking links) — upcoming soonest first, or
// the most recent past ones. Re-subscribes when `when` flips.
export function useBookedCalls(when = 'upcoming') {
  const { academyId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!academyId) { setLoading(false); return undefined; }
    setLoading(true);
    // An hour of grace so a call in progress still counts as upcoming.
    const cutoff = new Date(Date.now() - 60 * 60 * 1000);
    const q = when === 'past'
      ? query(collection(db, 'leadBotBookings'), where('academyId', '==', academyId), where('start', '<', cutoff), orderBy('start', 'desc'), limit(100))
      : query(collection(db, 'leadBotBookings'), where('academyId', '==', academyId), where('start', '>=', cutoff), orderBy('start', 'asc'), limit(200));
    return subscribeCollection(q, setRows, setLoading);
  }, [academyId, when]);
  return { rows, loading };
}
